import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Minus, X } from 'lucide-react';
export default function RoomSelector({ rooms, setRooms }) {
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowModal(false);
      }
    };
    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);
  const addRoom = () => {
    setRooms([...rooms, { adults: 2, children: [] }]);
  };
  const removeRoom = (idx) => {
    setRooms(rooms.filter((_, i) => i !== idx));
  };
  const updateAdults = (idx, delta) => {
    const newRooms = [...rooms];
    newRooms[idx].adults = Math.max(1, newRooms[idx].adults + delta);
    setRooms(newRooms);
  };
  const updateChildren = (idx, delta) => {
    const newRooms = [...rooms];
    if (delta > 0) {
      newRooms[idx].children.push(5);
    } else if (newRooms[idx].children.length > 0) {
      newRooms[idx].children.pop();
    }
    setRooms(newRooms);
  };
  const updateChildAge = (roomIdx, childIdx, age) => {
    const newRooms = [...rooms];
    newRooms[roomIdx].children[childIdx] = parseInt(age);
    setRooms(newRooms);
  };
  const totalAdults = rooms.reduce((sum, r) => sum + r.adults, 0);
  const totalChildren = rooms.reduce((sum, r) => sum + r.children.length, 0);
  
  return (
    <div className="relative" ref={modalRef}>
      <button
        type="button"
        onClick={() => setShowModal(!showModal)}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-left hover:border-[#FF7B47] transition flex items-center justify-between bg-white"
      >
        <span className="text-gray-700">
          {rooms.length} Chambre{rooms.length > 1 ? 's' : ''}, {totalAdults} Adulte{totalAdults > 1 ? 's' : ''}
          {totalChildren > 0 && `, ${totalChildren} Enfant${totalChildren > 1 ? 's' : ''}`}
        </span>
        <ChevronDown size={20} className="text-gray-400" />
      </button>
      {showModal && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl z-50 p-4 max-h-96 overflow-y-auto">
          {rooms.map((room, idx) => (
            <div key={idx} className="border-b pb-4 mb-4 last:border-b-0">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[#2B4979]">Chambre {idx + 1}</h4>
                {rooms.length > 1 && (
                  <button
                    onClick={() => removeRoom(idx)}
                    className="text-red-500 hover:bg-red-50 p-1 rounded"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm">Adultes</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateAdults(idx, -1)}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-8 text-center font-semibold">{room.adults}</span>
                  <button
                    onClick={() => updateAdults(idx, 1)}
                    className="w-8 h-8 rounded-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white flex items-center justify-center"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm">Enfants</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateChildren(idx, -1)}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-8 text-center font-semibold">{room.children.length}</span>
                  <button
                    onClick={() => updateChildren(idx, 1)}
                    className="w-8 h-8 rounded-full bg-[#FF7B47] hover:bg-[#ff6a30] text-white flex items-center justify-center"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              {room.children.length > 0 && (
                <div className="space-y-2 mt-3 pl-4 border-l-2 border-gray-200">
                  {room.children.map((age, childIdx) => (
                    <div key={childIdx} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">Âge enfant {childIdx + 1}:</span>
                      <select
                        value={age}
                        onChange={(e) => updateChildAge(idx, childIdx, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        {[...Array(12)].map((_, i) => {
                          const childAge = i + 1;
                          return (
                            <option key={childAge} value={childAge}>
                              {childAge} an{childAge > 1 ? 's' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button
            onClick={addRoom}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-[#FF7B47] hover:bg-gray-50 transition"
          >
            + Ajouter une chambre
          </button>
        </div>
      )}
    </div>
  );
}